import {
  EngagementStatus,
  PayableStatus,
  PayeeEngagement,
  PayeeEngagementStatus
} from "@wingspanhq/wingspan-api-sdk";
import { WSActionsMenuItem } from "@wingspanhq/fe-component-library/dist/lib/components/WSActions/WSActions";
import { useArchiveEngagementModal } from "../modals/useArchivalEngagementModal";
import { useDeleteEngagementModal } from "../modals/useDeletionEngagementModal";
import { useArchivalNotAllowedModal } from "../modals/useArchivalNotAllowedModal";
import { useDeletionNotAllowedModal } from "../modals/useDeletionNotAllowedModal";
import { useRestoreEngagementModal } from "../modals/useRestorationEngagementModal";
import { useRestorationNotAllowedModal } from "../modals/useRestorationNotAllowedModal";
import {
  queriesEngagements,
  queriesPayableRows,
  queriesWorkLogs
} from "@query";
import { useXWingspanPayerId } from "../../Payees/routes/RoutePayeeDetails/useXWingspanPayerId";

type Props = {
  payeeId: string;
  payeeName: string;
  engagement: PayeeEngagement;
};

export const useEngagementDefaultActions = () => {
  const archiveEngagementModal = useArchiveEngagementModal();
  const deleteEngagementModal = useDeleteEngagementModal();
  const archivalNotAllowedModal = useArchivalNotAllowedModal();
  const deletionNotAllowedModal = useDeletionNotAllowedModal();
  const restoreEngagementModal = useRestoreEngagementModal();
  const restorationNotAllowedModal = useRestorationNotAllowedModal();

  const xWingspanPayerId = useXWingspanPayerId();

  return async (props: Props) => {
    const actions: WSActionsMenuItem[] = [];

    // Payable guards, on v3 `searchPayableRows`. `listPayables` cannot serve these:
    // its filter has no per-assignment field, while the search row does
    // (`filter.payeeEngagementId`). v1 filtered on `memberClientId` with the v3
    // payee-engagement id — an id-space mismatch that could never have matched even
    // before `paymentsService` started throwing `NotMigrated`.
    //
    // `page.size: 1` keeps this a count query: the totals come from
    // `pagination.totalSize` (the v3 twin of v1's `summary.listSize`), not the rows.
    const activePayables =
      await queriesPayableRows.searchPayableRows.fetchQueryData({
        filter: {
          payeeEngagementId: { eq: props.engagement.id },
          // v3 has no `Overdue` status — an overdue payable stays `Opened`, so the
          // v1 triple (Pending/Open/Overdue) collapses to these two. `Created`
          // (draft) is deliberately excluded, matching v1.
          status: { anyOf: [PayableStatus.PENDING, PayableStatus.OPENED] }
        },
        page: { size: 1 }
      });

    const relatedPayables =
      await queriesPayableRows.searchPayableRows.fetchQueryData({
        filter: { payeeEngagementId: { eq: props.engagement.id } },
        page: { size: 1 }
      });

    const engagementType = props.engagement.engagementId
      ? await queriesEngagements.getEngagement.fetchQueryData({
          engagementId: props.engagement.engagementId,
          xWingspanAccount: xWingspanPayerId
        })
      : undefined;

    // TODO(v3): work-log fan-out for the delete-guard now reads v3 listWorkLogs by
    // `payeeEngagementId` (v1 `payerPayeeEngagementId`). v1↔v3 relationship id-space
    // alignment is unverified (runtime risk).
    const workLogsList = await queriesWorkLogs.listWorkLogs.fetchQueryData({
      filter: {
        payeeEngagementId: { eq: props.engagement.id }
      }
    });
    const workLogs = workLogsList.data ?? [];

    const hasActivePayables = (activePayables.pagination?.totalSize ?? 0) > 0;
    const hasAnyPayables = (relatedPayables.pagination?.totalSize ?? 0) > 0;
    const engagementTypeIsActive =
      engagementType?.status === EngagementStatus.ACTIVE;
    const hasActiveLogs = workLogs.length > 0;

    // Archive == `suspend`, and v3 only allows that from `Activated`. A
    // `Created` assignment (the state every assignment is born in — there is no
    // create-time activation) has never been active, so there is nothing to
    // suspend: the API answers `409 payeeEngagement.InvalidLifecycleTransition`.
    // `Delete` below is the supported action for one, and it is already offered
    // unconditionally.
    //
    // `Restore` == `activate`, documented as valid "From Created or Suspended",
    // but on a brand-new assignment it is refused too — `409
    // payeeEngagement.EligibilityIncomplete` — because its requirements are not
    // complete. So `Created` offers NEITHER: only Delete applies.
    //
    // Probed against the live API in
    // `src/v3/api/tests/specs/payeeEngagementLifecycleProbe.itest.ts`:
    //   suspend(from Created)  -> 409 InvalidLifecycleTransition
    //   activate(from Created) -> 409 EligibilityIncomplete
    //   delete(from Created)   -> OK
    // (WIN-15926. An earlier note here claimed `Created` "counts as live
    // alongside `Activated`" — that produced an action that always errored.)
    const isCreated = props.engagement.status === PayeeEngagementStatus.CREATED;
    const isLive = props.engagement.status === PayeeEngagementStatus.ACTIVATED;

    if (isCreated) {
      // No archive/restore — fall through to Delete only.
    } else if (isLive) {
      if (hasActivePayables) {
        actions.push({
          disabled: true,
          onDisabledClick: async () => {
            await archivalNotAllowedModal.open({
              payeeId: props.payeeId,
              payeeName: props.payeeName,
              engagement: props.engagement
            });
          },
          label: "Archive",
          icon: "archive"
        });
      } else {
        actions.push({
          onClick: async () => {
            await archiveEngagementModal.open({
              payeeId: props.payeeId,
              payeeName: props.payeeName,
              engagement: props.engagement
            });
          },
          label: "Archive",
          icon: "archive"
        });
      }
    } else {
      if (engagementTypeIsActive || !props.engagement.engagementId) {
        actions.push({
          onClick: async () => {
            await restoreEngagementModal.open({
              payeeId: props.payeeId,
              payeeName: props.payeeName,
              engagement: props.engagement
            });
          },
          label: "Restore",
          icon: "redo"
        });
      } else {
        actions.push({
          disabled: true,
          onDisabledClick: async () => {
            await restorationNotAllowedModal.open({
              payeeId: props.payeeId,
              payeeName: props.payeeName,
              engagement: props.engagement
            });
          },
          label: "Restore",
          icon: "redo"
        });
      }
    }

    if (hasAnyPayables || hasActiveLogs) {
      actions.push({
        disabled: true,
        onDisabledClick: async () => {
          await deletionNotAllowedModal.open({
            payeeId: props.payeeId,
            payeeName: props.payeeName,
            engagement: props.engagement
          });
        },
        label: "Delete",
        icon: "trash"
      });
    } else {
      actions.push({
        onClick: async () => {
          await deleteEngagementModal.open({
            payeeId: props.payeeId,
            payeeName: props.payeeName,
            engagement: props.engagement
          });
        },
        label: "Delete",
        icon: "trash",
        destructive: true
      });
    }

    return actions;
  };
};
